import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Film, Loader2, Repeat, Sparkles } from 'lucide-react';
import Sheet from './Sheet';
import DemoOverlay from './DemoOverlay';
import { Chip } from './parts';
import { resolveDemo } from './demoRegistry';
import { SUBSTITUTE_REASONS, bestAlternativeId, isSuggestedByEngine, safeMediaUrl, sortAlternatives } from './playerLogic';
import { exerciseLibrary, workoutExecution } from '../../services/api';

// "Substituir exercício": ver as alternativas QUE O SERVIDOR PROPÕE — as curadas por uma pessoa e os equivalentes
// propostos pelo motor (mesmo grupo muscular), sempre com o motivo escrito —, espreitar músculos, equipamento e
// demonstração de cada uma, confirmar, e continuar o treino com a escolhida. A troca é SÓ desta sessão: o plano do
// atleta não muda. Escolhe-se o que o ginásio consegue servir (equipamento disponível) e ainda não está neste treino; o
// resto aparece, mas indica porquê. Quem escolhe é o atleta: nada aqui é aplicado sozinho.
//
// Os equivalentes do motor levam o aviso de que ainda não foram confirmados por uma pessoa na biblioteca — é
// INFORMAÇÃO, não impedimento: o servidor aceita a troca por eles (mesmo grupo muscular, equipamento disponível, ainda
// não usado neste treino). Se recusar, a mensagem fica à vista e o atleta pode repetir.

/** A ficha da biblioteca → o formato que a demonstração consome. */
const libraryToDemoSource = (detail) => ({
  name: detail.name,
  muscles: detail.muscles || [],
  equipment: detail.equipmentLinks || [],
  media: detail.media || [],
  steps: detail.steps || [],
  mistakes: detail.mistakes || [],
  tip: detail.tip || null,
  imageUrl: detail.imageUrl, videoUrl: detail.videoUrl, gifUrl: detail.gifUrl,
});

/**
 * Uma sugestão do servidor (`SubstituteDto`) no formato que a lista desenha: o `exerciseId` que o servidor devolve é
 * o Id da biblioteca, que é o que a troca usa.
 */
export const asSuggestion = (item) => ({
  libraryExerciseId: item.exerciseId,
  name: item.name,
  kind: item.kind,
  kindLabel: item.kindLabel,
  note: item.note || null,
  source: item.source || null,
  imageUrl: item.imageUrl || null,
  available: item.available !== false,
  equipmentNames: item.equipmentNames || [],
  isPlanned: false,
});

/** Só uma sugestão por exercício (o exercício do plano pode voltar a aparecer na lista). */
const distinctByExercise = (items) => {
  const seen = new Set();
  return items.filter((a) => {
    if (!a?.libraryExerciseId || seen.has(a.libraryExerciseId)) return false;
    seen.add(a.libraryExerciseId);
    return true;
  });
};

// Identidades fixas: o efeito que carrega as sugestões não pode repetir o pedido a cada desenho.
const detailOf = (id) => workoutExecution.libraryExercise(id);
const suggestedOf = (id, take = 6) => exerciseLibrary.suggestedSubstitutes(id, take);

// O que se diz quando não há alternativas — por CONTEXTO, porque os dois sítios não oferecem as mesmas saídas:
//   · treino em curso: pode continuar com o exercício do plano ou saltá-lo (as duas coisas existem nesta sessão);
//   · plano do atleta: não há «saltar» nenhum num plano que se está a editar, e a troca fica guardada no plano.
export const EMPTY_IN_SESSION = 'O ginásio ainda não indicou alternativas para ele. Continue com o exercício do plano ou salte-o.';
export const EMPTY_IN_PLAN = 'O ginásio ainda não indicou alternativas para ele. Pode manter este exercício no plano ou removê-lo, se preferir.';

export default function SubstituteSheet({
  open, exercise, onClose, onConfirm, busy, inSessionIds = [], loadDetail = detailOf, loadSuggested = suggestedOf,
  // O mesmo painel serve o treino em curso e o PLANO do atleta. No plano a troca fica guardada no plano e não há
  // «motivo» a enviar: o endpoint da troca só leva o exercício da biblioteca — mostrar o motivo seria um controlo sem efeito.
  subtitle, footerNote, allowReason = true, emptyHint,
}) {
  const [selected, setSelected] = useState(null);
  const [reason, setReason] = useState(null);
  const [message, setMessage] = useState(null);
  const [details, setDetails] = useState({});      // id → { loading, error, demo }
  const [demoFor, setDemoFor] = useState(null);
  const [suggestions, setSuggestions] = useState({ state: 'loading', items: [] });

  // O exercício que o plano mandava fazer é o que tem alternativas (e é ele que o servidor valida na troca); num
  // exercício acrescentado a meio do treino, o da biblioteca é o que lá está.
  const libraryId = exercise?.plannedLibraryExerciseId ?? exercise?.executedLibraryExerciseId ?? exercise?.library?.id ?? null;

  const load = useCallback(() => {
    if (!libraryId) { setSuggestions({ state: 'none', items: [] }); return; }
    setSuggestions({ state: 'loading', items: [] });
    loadSuggested(libraryId, 6)
      .then(({ data }) => setSuggestions({ state: 'ok', items: (data?.items || []).map(asSuggestion) }))
      .catch(() => setSuggestions({ state: 'error', items: [] }));
  }, [libraryId, loadSuggested]);

  useEffect(() => {
    if (!open) return;
    setSelected(null);
    setReason(null);
    setMessage(null);
    setDemoFor(null);
    load();
  }, [open, exercise?.id, load]);

  // Já se substituiu: o exercício do plano volta a estar à mão para se poder regressar a ele.
  const revert = useMemo(() => (exercise?.wasSubstituted && exercise?.plannedLibraryExerciseId ? [{
    libraryExerciseId: exercise.plannedLibraryExerciseId,
    name: exercise.plannedName || exercise.name || 'Exercício do plano',
    kind: 'planned', kindLabel: 'Exercício do plano',
    available: true, isPlanned: true, equipmentNames: [], imageUrl: null, note: null,
  }] : []), [exercise?.wasSubstituted, exercise?.plannedLibraryExerciseId, exercise?.plannedName, exercise?.name]);

  // Sem as sugestões completas ficam as curadas que o exercício já trazia: falha de rede não vira lista vazia.
  const alternatives = useMemo(() => {
    const fromServer = suggestions.state === 'error' ? (exercise?.alternatives || []) : suggestions.items;
    const items = distinctByExercise([...revert, ...fromServer]);
    // O exercício que se está a fazer agora não é alternativa a si mesmo.
    return sortAlternatives(items.filter((a) => a.libraryExerciseId !== exercise?.executedLibraryExerciseId));
  }, [suggestions, exercise?.alternatives, exercise?.executedLibraryExerciseId, revert]);

  const fetchDetail = async (id) => {
    if (details[id]?.demo || details[id]?.loading) return;
    setDetails((d) => ({ ...d, [id]: { loading: true } }));
    try {
      const res = await loadDetail(id);
      const source = libraryToDemoSource(res.data);
      setDetails((d) => ({ ...d, [id]: { demo: resolveDemo({ name: res.data.name }, source), muscles: source.muscles, equipment: source.equipment } }));
    } catch {
      setDetails((d) => ({ ...d, [id]: { error: true } }));
    }
  };

  // O que já está neste treino (noutro exercício) não se escolhe: o servidor recusa a troca por um duplicado. O
  // exercício do plano desta linha é a exceção — é por ele que se volta atrás depois de uma troca.
  const already = useMemo(() => new Set((inSessionIds || []).filter((x) => Number.isInteger(x))), [inSessionIds]);
  const here = (alt) => !alt.isPlanned && already.has(alt.libraryExerciseId);

  const select = (alt) => {
    // O que o ginásio não consegue servir e o que já está no treino não se escolhe (e diz-se porquê, item a item).
    if (!alt.available || here(alt)) return;
    setSelected(alt.libraryExerciseId);
    setMessage(null);
    fetchDetail(alt.libraryExerciseId);
  };

  const confirm = async () => {
    const alt = alternatives.find((a) => a.libraryExerciseId === selected);
    if (!alt) return;
    const result = await onConfirm(alt.libraryExerciseId, reason);
    if (!result.ok) setMessage(result.message || 'Não foi possível substituir o exercício.');
  };

  const chosen = alternatives.find((a) => a.libraryExerciseId === selected);
  // A melhor é uma CURADA disponível; o motor só ocupa esse lugar quando não há nenhuma curada disponível.
  const bestId = bestAlternativeId(alternatives);
  const hasEngine = alternatives.some(isSuggestedByEngine);
  const loading = suggestions.state === 'loading';

  return (
    <>
      <Sheet open={open} onClose={busy ? undefined : onClose} title="Substituir exercício" subtitle={subtitle ?? (exercise ? `Em vez de ${exercise.plannedName || exercise.name} · só neste treino` : '')} testId="substitute-sheet" dismissible={!busy}
        footer={(
          <div className="wp-sub-foot">
            {message ? <p className="wp-error" role="alert" data-testid="substitute-error">{message}</p> : null}
            <button type="button" className="wp-btn wp-btn-primary wp-btn-xl" onClick={confirm} disabled={!chosen || busy} data-testid="substitute-confirm">
              {busy ? <Loader2 size={20} className="wp-spin" aria-hidden="true" /> : <Repeat size={20} aria-hidden="true" />}
              {chosen ? `Usar ${chosen.name}` : 'Escolha uma alternativa'}
            </button>
            <p className="wp-note wp-center">{footerNote ?? 'O seu plano não é alterado.'}</p>
          </div>
        )}>
        {suggestions.state === 'error' ? (
          <div className="wp-sub-notice" role="alert" data-testid="substitute-load-error">
            <span>Não foi possível carregar as sugestões do servidor. Estão à vista as alternativas curadas desta sessão.</span>
            <button type="button" className="wp-btn wp-btn-ghost" onClick={load} data-testid="substitute-retry">Tentar novamente</button>
          </div>
        ) : null}

        {loading ? (
          <div className="wp-state-center wp-sub-empty" role="status" aria-busy="true" data-testid="substitute-loading">
            <Loader2 size={26} className="wp-spin" aria-hidden="true" />
            <p className="wp-muted">A carregar as sugestões do servidor…</p>
          </div>
        ) : alternatives.length === 0 ? (
          <div className="wp-state-center wp-sub-empty" data-testid="substitute-empty">
            <Repeat size={30} aria-hidden="true" />
            <p className="wp-demo-empty-title">Sem alternativas para este exercício</p>
            <p className="wp-muted">{emptyHint || EMPTY_IN_SESSION}</p>
          </div>
        ) : (
          <ul className="wp-alts" role="radiogroup" aria-label="Alternativas">
            {alternatives.map((alt) => {
              const on = alt.libraryExerciseId === selected;
              const info = details[alt.libraryExerciseId];
              const img = safeMediaUrl(alt.imageUrl);
              const engine = isSuggestedByEngine(alt);
              const off = !alt.available || here(alt);
              const thumb = <span className="wp-alt-thumb">{img ? <img src={img} alt="" loading="lazy" /> : <Repeat size={22} aria-hidden="true" />}</span>;
              const text = (
                <span className="wp-alt-text">
                  <span className="wp-alt-name">{alt.name}</span>
                  <span className="wp-alt-meta">
                    {alt.isPlanned ? 'Exercício do plano' : alt.kindLabel}{alt.note ? ` · ${alt.note}` : ''}
                  </span>
                  <span className="wp-alt-tags">
                    {alt.libraryExerciseId === bestId ? <Chip tone="good" icon={Sparkles}>Melhor alternativa</Chip> : null}
                    {engine ? <Chip tone="accent">Proposto pelo motor</Chip> : <Chip tone="plain">Escolhido por uma pessoa</Chip>}
                    {engine ? <Chip tone="warn">Ainda não confirmado</Chip> : null}
                    {!alt.available ? <Chip tone="warn">Equipamento indisponível neste ginásio</Chip> : null}
                    {here(alt) ? <Chip tone="warn">Já está neste treino</Chip> : null}
                    {alt.equipmentNames.slice(0, 2).map((n) => <Chip key={n}>{n}</Chip>)}
                  </span>
                </span>
              );

              return (
                <li key={alt.libraryExerciseId} className={`wp-alt ${on ? 'is-on' : ''} ${off ? 'is-off' : ''}`} data-testid={`alt-${alt.libraryExerciseId}`}>
                  {/* Toda a alternativa é um controlo a sério: as curadas e as do motor escolhem-se; as que o ginásio
                      não tem — ou que já estão neste treino — ficam desativadas e dizem porquê. */}
                  <button type="button" role="radio" aria-checked={on} className="wp-alt-main" onClick={() => select(alt)} aria-disabled={off}>
                    {thumb}{text}
                    <span className="wp-alt-tick" aria-hidden="true">{on ? <Check size={20} strokeWidth={3} /> : null}</span>
                  </button>

                  {on ? (
                    <div className="wp-alt-detail" data-testid="alt-detail">
                      {info?.loading ? <p className="wp-muted"><Loader2 size={14} className="wp-spin" aria-hidden="true" /> A carregar detalhes…</p> : null}
                      {info?.error ? <p className="wp-muted">Não foi possível carregar os detalhes. Pode escolher na mesma.</p> : null}
                      {info?.demo ? (
                        <>
                          {info.muscles.length > 0 ? (
                            <div className="wp-chip-row"><span className="wp-chip-label">Músculos</span>{info.muscles.slice(0, 5).map((m) => <Chip key={m.id} tone={m.role === 'primary' ? 'accent' : 'plain'}>{m.name}</Chip>)}</div>
                          ) : null}
                          {info.equipment.length > 0 ? (
                            <div className="wp-chip-row"><span className="wp-chip-label">Equipamento</span>{info.equipment.map((e) => <Chip key={e.id}>{e.name}</Chip>)}</div>
                          ) : null}
                          <button type="button" className="wp-btn wp-btn-ghost" onClick={() => setDemoFor(alt.libraryExerciseId)} data-testid="alt-demo"><Film size={16} aria-hidden="true" /> Ver demonstração</button>
                        </>
                      ) : null}
                      {allowReason ? (
                        <div className="wp-chip-row wp-reasons" role="group" aria-label="Motivo (opcional)">
                          <span className="wp-chip-label">Motivo</span>
                          {SUBSTITUTE_REASONS.map((r) => (
                            <button key={r} type="button" className={`wp-rpe-chip wp-reason ${reason === r ? 'is-on' : ''}`} aria-pressed={reason === r} onClick={() => setReason(reason === r ? null : r)}>{r}</button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        {hasEngine && !loading ? (
          <p className="wp-note wp-muted" data-testid="substitute-engine-note">
            Os exercícios com «Proposto pelo motor» foram propostos automaticamente, do mesmo grupo muscular, e ainda não
            foram confirmados por uma pessoa na biblioteca. Pode escolher qualquer um deles que o ginásio consiga servir e
            que ainda não esteja no treino.
          </p>
        ) : null}
      </Sheet>

      <DemoOverlay open={demoFor != null} demo={demoFor != null ? details[demoFor]?.demo : null} onClose={() => setDemoFor(null)} stacked />
    </>
  );
}
